import React, { useMemo } from 'react';
import { TeamSelect } from '@kineticdata/react';
import {
  Status,
  SuggestionsContainer,
  generateInput,
  generateMultipleSelectionsContainer,
  generateSingleSelectionsContainer,
  generateMultipleSelection,
  generateSingleSelection,
} from './TypeaheadDefaults';
import { FieldWrapper } from './FieldWrapper';
import classNames from 'classnames';
import { parseRenderOptions } from './utils';

const selectionToValue = selection => selection.get('name');

const splitTeamName = team => {
  const [local, ...parents] = team
    .get('name')
    .split('::')
    .reverse();
  return [parents.reverse().join('::'), local];
};

const Suggestion = ({ suggestion, active }) => {
  const [parent, local] = splitTeamName(suggestion);
  return (
    <div className={classNames('suggestion', { active })}>
      {parent && <div className="small">{parent}</div>}
      <div className="large">{local}</div>
    </div>
  );
};

const TeamComponent = ({ multiple, renderOptions, components, ...props }) => (
  <FieldWrapper {...props} renderOptions={renderOptions} fieldset={multiple}>
    <TeamSelect
      components={{ Status, SuggestionsContainer, Suggestion, ...components }}
      disabled={!props.enabled}
      minSearchLength={1}
      multiple={multiple}
      id={props.id}
      value={props.value}
      onChange={props.onChange}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      placeholder={renderOptions.placeholder}
      form={props.form}
      invalid={renderOptions.hasErrors}
    />
  </FieldWrapper>
);

/******************************************************************************
 * Single Field
 ******************************************************************************/

export const TeamField = props => {
  const renderOptions = parseRenderOptions(props);
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateSingleSelection(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: generateSingleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );

  return (
    <TeamComponent
      renderOptions={renderOptions}
      components={components}
      {...props}
    />
  );
};

/******************************************************************************
 * Multi Field
 ******************************************************************************/

export const TeamMultiField = props => {
  const renderOptions = parseRenderOptions(props, { isFloating: false });
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateMultipleSelection(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: generateMultipleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );
  return (
    <TeamComponent
      multiple={true}
      renderOptions={renderOptions}
      components={components}
      {...props}
    />
  );
};
